import React, { useContext, useMemo, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import { useGetVersion, useUpdateSettings } from "@/hooks";
import { enqueueSnackbar } from "notistack";
import { Box } from "@mui/system";
import { Button, Grid, TextField, Typography } from "@mui/material";

export const DeveloperVersion = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { settings } = useGetVersion();
  const { saveSettings } = useUpdateSettings(
    () => {
      enqueueSnackbar(gettext("The deal has updated"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(error, {
          variant: "error",
        });
      }
    },
  );

  const [newMajor, setNewMajor] = useState(-1);

  const [newMinor, setNewMinor] = useState(-1);

  const [newRelease, setNewRelease] = useState(-1);

  const major = useMemo(() => {
    let returnValue;
    if (newMajor > -1) {
      returnValue = newMajor;
    } else {
      returnValue = settings?.major || 0;
    }
    return returnValue;
  }, [newMajor, settings.major]);

  const minor = useMemo(() => {
    let returnValue;
    if (newMinor > -1) {
      returnValue = newMinor;
    } else {
      returnValue = settings?.minor || 0;
    }
    return returnValue;
  }, [newMinor, settings.minor]);

  const release = useMemo(() => {
    let returnValue;
    if (newRelease > -1) {
      returnValue = newRelease;
    } else {
      returnValue = settings?.release || 0;
    }
    return returnValue;
  }, [newRelease, settings.release]);

  const updatedVersion = useMemo(() => {
    return `${settings?.major}.${settings?.minor}.${settings?.release}`;
  }, [settings]);

  const handleChangeMajor = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewMajor(Number(event.target.value));
  };

  const handleChangeMinor = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewMinor(Number(event.target.value));
  };

  const handleChangeRelease = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewRelease(Number(event.target.value));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const variables = {
      key: "version",
      data: JSON.stringify({
        major,
        minor,
        release,
      }),
    };

    saveSettings(variables).then();
  };

  return (
    <Grid
      container
      sx={{ alignItems: "center", paddingTop: 3, paddingBottom: 3 }}
    >
      {/* Left Column */}
      <Grid size={{ xs: 12 }}>
        <Grid
          style={{
            textAlign: "left",
            paddingLeft: "10px",
          }}
        >
          <Typography variant="h4">{gettext("Version Management")}</Typography>
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 12,
        }}
        sx={{
          p: 1,
        }}
      >
        <Typography variant="body1">
          {gettext(
            "Use this page to update the application version. Once you change the version, the client-side application will automatically download and run the latest code from the server.",
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 4 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            mt: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                required
                fullWidth
                id="major"
                label="Major"
                name="major"
                type="number"
                value={major}
                onChange={handleChangeMajor}
                slotProps={{
                  htmlInput: {
                    min: 0,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                required
                fullWidth
                id="minor"
                label="Minor"
                name="minor"
                type="number"
                value={minor}
                onChange={handleChangeMinor}
                slotProps={{
                  htmlInput: {
                    min: 0,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                required
                fullWidth
                id="release"
                label="Release"
                name="release"
                type="number"
                value={release}
                onChange={handleChangeRelease}
                slotProps={{
                  htmlInput: {
                    min: 0,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
            }}
          >
            Update Version
          </Button>
        </Box>
        {updatedVersion && (
          <Typography
            variant="h6"
            component="h2"
            sx={{
              mt: 4,
            }}
          >
            Current Version: {updatedVersion}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
